import { Export } from "@phosphor-icons/react";
import { toast } from "react-toastify";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import { DialogFooter } from "common/components/ui/Dialog";
import { DrawerFooter } from "common/components/ui/Drawer";
import useLinkedInUrls from "common/hooks/useLinkedInUrls";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { LinkedInProfile } from "common/types";

import useBlacklistMutations from "../../datahooks/useBlacklistMutations";
import CSVFileUpload from "./CSVFileUpload";

const maxUrls = 1000;
export default function CsvUpload({
  failedUrls,
  onGetProfiles,
}: {
  failedUrls: string[] | null;
  onGetProfiles: (profiles: LinkedInProfile[], failed: string[]) => void;
}) {
  const isDesktop = useTwBreakpoint("lg");
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const { linkedInUrls, setLinkedInUrls, validUrls, invalidLines } =
    useLinkedInUrls();
  const { getProfiles, isGettingProfiles } = useBlacklistMutations();

  function onNext() {
    getProfiles({
      identifiers: validUrls.map((url) => {
        const urlComponents = url.split("/");
        return urlComponents[
          urlComponents.findIndex((string) => string === "in") + 1
        ];
      }),
    })
      .then(({ profiles, failed }) => {
        onGetProfiles(profiles, failed);
      })
      .catch(() => {
        toast.error("Failed to validate LinkedIn urls");
      });
  }

  let errorLines: number[] = [];
  if (invalidLines.length) {
    errorLines = invalidLines;
  } else if (failedUrls) {
    errorLines = failedUrls.map((_, index) => index + 1);
  }

  return (
    <>
      <div className="px-6">
        <CSVFileUpload
          lineItems={failedUrls || linkedInUrls}
          setLineItems={setLinkedInUrls}
          errorLines={errorLines}
          maxAllowedItems={maxUrls}
          footerContent={
            failedUrls && (
              <Button
                className="ml-auto mt-2"
                variant="secondary-purple"
                leftIcon={<Export />}
                asChild
              >
                <a
                  href={`data:text/csv;charset=utf-8,${failedUrls.join(",")}`}
                  download="failed_links.csv"
                >
                  Export ({failedUrls.length})
                </a>
              </Button>
            )
          }
        />
      </div>
      <RenderIf condition={!failedUrls}>
        <Footer className="justify-end">
          <Button
            disabled={!validUrls.length || validUrls.length > maxUrls}
            isLoading={isGettingProfiles}
            onClick={onNext}
            data-cy="blacklist-csv-next-button"
          >
            Next
          </Button>
        </Footer>
      </RenderIf>
    </>
  );
}
